import {FetchBaseQueryError} from '@reduxjs/toolkit/query';
import {DataStatus, FormSubmitHandler} from 'platform/components';

import {omit} from 'ramda';

import {useEditConsentTypeMutation, useGetConsentTypeQuery} from '@dms/api/customer';
import {EditConsentTypeRequestBody, EditTemplateRequestBody} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {useNavigate} from 'shared/web';

import {ConsentTypeFormType} from '../../../components/GdprConsentTypeForm/components/ConsentTypeFormTemplate';
import {GdprConsentTypeForm} from '../../../components/GdprConsentTypeForm/GdprConsentTypeForm';
import {getConsentTypeRecipientFields} from '../../../utils/getConsentTypeRecipientFields';

interface EditGdprConsentTypeFormProps {
  id: string;
  cancelRoute: string;
}

export function EditGdprConsentTypeForm(props: EditGdprConsentTypeFormProps) {
  const navigate = useNavigate();
  const {data: consentType, isLoading, isError} = useGetConsentTypeQuery({consentTypeId: props.id});
  const [edit] = useEditConsentTypeMutation();

  const onSubmit: FormSubmitHandler<ConsentTypeFormType> = async (
    values,
    setErrors,
    _reset,
    blockerContext
  ) => {
    const templates = values.templates.map(
      omit(['fileUri', 'fileName'])
    ) as EditTemplateRequestBody[];

    const body: EditConsentTypeRequestBody = {
      name: values.name,
      subject: values.subject,
      duration: values.duration,
      channels: values.channels,
      templates,
      ...getConsentTypeRecipientFields(values),
    };

    await edit({consentTypeId: props.id, editConsentTypeRequestBody: body})
      .unwrap()
      .then(() => {
        if (blockerContext?.wasSubmittedFromBlocker) {
          return;
        }
        navigate(props.cancelRoute);
      })
      .catch((error: FetchBaseQueryError) =>
        handleApiError(error, {
          silentOnValidationError: true,
          setErrors,
          signalApiError: blockerContext?.reportApiFailure,
          silent: blockerContext?.wasSubmittedFromBlocker,
        })
      );
  };

  return (
    <DataStatus isLoading={isLoading} isError={isError}>
      <GdprConsentTypeForm
        onSubmit={onSubmit}
        defaultValues={{
          ...consentType,
          recipientCustomerId: consentType?.recipient?.customerId ?? null,
        }}
        recipient={consentType?.recipient}
        cancelRoute={props.cancelRoute}
        submitTitle={i18n.t('general.actions.save')}
      />
    </DataStatus>
  );
}
